import React, { useState, useEffect } from 'react';
import { Page } from '../types';

interface NavbarProps {
  currentPage: Page;
  setPage: (page: Page) => void;
}

const Navbar: React.FC<NavbarProps> = ({ currentPage, setPage }) => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isDark, setIsDark] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 30);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setIsDark(document.documentElement.classList.contains('dark'));
  }, []);

  const toggleDarkMode = () => {
    const isNowDark = document.documentElement.classList.toggle('dark');
    setIsDark(isNowDark);
  };

  const navLinks: { label: string; page: Page }[] = [
    { label: 'About', page: 'about' },
    { label: 'Services', page: 'services' },
    { label: 'How It Works', page: 'how-it-works' },
    { label: 'Knowledge Hub', page: 'resources' },
    { label: 'Contact', page: 'contact' },
  ];

  const handlePageChange = (page: Page) => {
    setPage(page);
    setIsMobileMenuOpen(false);
  };

  return (
    <nav className={`fixed top-0 w-full z-[100] transition-all duration-700 ${isScrolled ? 'bg-white/80 dark:bg-slate-900/80 backdrop-blur-2xl border-b border-slate-200/50 dark:border-slate-800/50 py-2 shadow-premium' : 'bg-transparent py-5'}`}>
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="flex justify-between items-center">
          <div onClick={() => handlePageChange('home')} className="flex items-center group cursor-pointer z-[110]">
            <img 
              src="https://res.cloudinary.com/dohuj4mx9/image/upload/v1769023542/fintrack_logo_unique_v1-removebg-preview_oc4bqx.png" 
              alt="FinTrack Logo" 
              className={`object-contain transition-all duration-700 ease-out group-hover:scale-105 ${isScrolled ? 'w-14 h-14 sm:w-16 sm:h-16' : 'w-20 h-20 sm:w-24 sm:h-24'}`}
            />
          </div>
          
          <div className="hidden xl:flex items-center space-x-2">
            {navLinks.map((link) => (
              <button 
                key={link.page}
                onClick={() => handlePageChange(link.page)}
                className={`text-sm font-bold tracking-tight transition-all px-5 py-2.5 rounded-full relative group/link ${currentPage === link.page ? 'text-primary dark:text-accent-300' : 'text-slate-600 dark:text-slate-400 hover:text-primary dark:hover:text-accent-200'}`}
              >
                {link.label}
                <span className={`absolute bottom-1 left-1/2 -translate-x-1/2 w-1 h-1 bg-primary rounded-full transition-all duration-500 ${currentPage === link.page ? 'opacity-100 scale-100' : 'opacity-0 scale-0 group-hover/link:opacity-100 group-hover/link:scale-100'}`}></span>
              </button>
            ))}
          </div>

          <div className="flex items-center gap-4 sm:gap-6 z-[110]">
            <button 
              onClick={toggleDarkMode}
              className="w-11 h-11 flex items-center justify-center rounded-full bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-400 hover:scale-110 active:scale-95 transition-all shadow-inner"
              aria-label="Toggle Dark Mode"
            >
              <span className="material-symbols-rounded text-2xl leading-none">
                {isDark ? 'light_mode' : 'dark_mode'}
              </span>
            </button>
            <button 
              onClick={() => handlePageChange('booking')}
              className="hidden sm:block btn-premium bg-primary text-white px-8 py-3.5 text-xs font-black uppercase tracking-[0.2em] rounded-[18px] shadow-premium active:scale-95 transition-all"
            >
              Get Started
            </button>
            
            <button 
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              className="xl:hidden w-11 h-11 flex items-center justify-center rounded-full bg-primary/10 text-primary dark:text-white hover:scale-110 active:scale-95 transition-all"
            >
              <span className="material-symbols-rounded text-3xl leading-none">
                {isMobileMenuOpen ? 'close' : 'menu'}
              </span>
            </button>
          </div>
        </div>
      </div>

      {/* Reimagined Mobile Menu */}
      <div className={`xl:hidden fixed inset-0 z-[100] transition-all duration-700 ease-[cubic-bezier(0.16,1,0.3,1)] ${isMobileMenuOpen ? 'translate-y-0' : '-translate-y-full'}`}>
        <div className="absolute inset-0 bg-white/95 dark:bg-slate-950/95 backdrop-blur-3xl"></div>
        <div className="relative flex flex-col items-center justify-center h-full space-y-10 px-8 text-center">
          {navLinks.map((link, i) => (
            <button 
              key={link.page}
              onClick={() => handlePageChange(link.page)}
              className={`text-4xl font-display font-bold transition-all duration-700 delay-[${i * 50}ms] ${isMobileMenuOpen ? 'translate-y-0 opacity-100' : 'translate-y-12 opacity-0'} ${currentPage === link.page ? 'text-primary dark:text-accent-400 scale-110' : 'text-slate-600 dark:text-slate-400 hover:text-primary'}`}
            >
              {link.label}
            </button>
          ))}
          <button 
            onClick={() => handlePageChange('booking')}
            className={`w-full max-w-sm bg-primary text-white py-6 rounded-[28px] font-black text-xl shadow-2xl shadow-primary/30 transition-all duration-700 ${isMobileMenuOpen ? 'translate-y-0 opacity-100' : 'translate-y-12 opacity-0'}`}
          >
            Schedule Session
          </button>
        </div>
      </div>
    </nav>
  );
};

export default Navbar;